import { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import ActionFxHost from "@/components/ActionFx";
import Landing from "@/pages/Landing";
import AdminLogin from "@/pages/AdminLogin";
import POS from "@/pages/POS";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Products = lazy(() => import("@/pages/Products"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const Purchases = lazy(() => import("@/pages/Purchases"));
const StockLedger = lazy(() => import("@/pages/StockLedger"));
const Sales = lazy(() => import("@/pages/Sales"));
const Returns = lazy(() => import("@/pages/Returns"));
const Customers = lazy(() => import("@/pages/Customers"));
const Suppliers = lazy(() => import("@/pages/Suppliers"));
const Reports = lazy(() => import("@/pages/Reports"));
const DiscountReport = lazy(() => import("@/pages/DiscountReport"));
const NotificationsPage = lazy(() => import("@/pages/Notifications"));
const InventoryReports = lazy(() => import("@/pages/InventoryReports"));
const AuditLogs = lazy(() => import("@/pages/AuditLogs"));
const Backup = lazy(() => import("@/pages/Backup"));
const Expenses = lazy(() => import("@/pages/Expenses"));
const OpeningStock = lazy(() => import("@/pages/OpeningStock"));
const LabelBatch = lazy(() => import("@/pages/LabelBatch"));
const Receipt = lazy(() => import("@/pages/Receipt"));
const DangerZone = lazy(() => import("@/pages/DangerZone"));
const UserManagement = lazy(() => import("@/pages/UserManagement"));
const ActivityLogs = lazy(() => import("@/pages/ActivityLogs"));
const Deliveries = lazy(() => import("@/pages/Deliveries"));
const BillingAgents = lazy(() => import("@/pages/BillingAgents"));
const BulkImport = lazy(() => import("@/pages/BulkImport"));
const ActivityFeed = lazy(() => import("@/pages/ActivityFeed"));
const SettingsPage = lazy(() => import("@/pages/Settings"));

const Loading = () => (
  <div className="flex min-h-screen items-center justify-center">
    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
  </div>
);

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <ActionFxHost />
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/pos" element={<POS />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<Protected><Dashboard /></Protected>} />
              <Route path="/admin/products" element={<Protected><Products /></Protected>} />
              <Route path="/admin/inventory" element={<Protected><Inventory /></Protected>} />
              <Route path="/admin/inventory-reports" element={<Protected><InventoryReports /></Protected>} />
              <Route path="/admin/purchases" element={<Protected><Purchases /></Protected>} />
              <Route path="/admin/stock-ledger" element={<Protected><StockLedger /></Protected>} />
              <Route path="/admin/sales" element={<Protected><Sales /></Protected>} />
              <Route path="/admin/returns" element={<Protected><Returns /></Protected>} />
              <Route path="/admin/customers" element={<Protected><Customers /></Protected>} />
              <Route path="/admin/suppliers" element={<Protected><Suppliers /></Protected>} />
              <Route path="/admin/reports" element={<Protected><Reports /></Protected>} />
              <Route path="/admin/discount-report" element={<Protected><DiscountReport /></Protected>} />
              <Route path="/admin/notifications" element={<Protected><NotificationsPage /></Protected>} />
              <Route path="/admin/audit-logs" element={<Protected><AuditLogs /></Protected>} />
              <Route path="/admin/opening-stock" element={<Protected><OpeningStock /></Protected>} />
              <Route path="/admin/label-batch" element={<Protected><LabelBatch /></Protected>} />
              <Route path="/receipt/:invoiceNo" element={<Receipt />} />
              <Route path="/admin/danger-zone" element={<Protected><DangerZone /></Protected>} />
              <Route path="/admin/users" element={<Protected><UserManagement /></Protected>} />
              <Route path="/admin/activity-logs" element={<Protected><ActivityLogs /></Protected>} />
              <Route path="/admin/deliveries" element={<Protected><Deliveries /></Protected>} />
              <Route path="/admin/billing-agents" element={<Protected><BillingAgents /></Protected>} />
              <Route path="/admin/bulk-import" element={<Protected><BulkImport /></Protected>} />
              <Route path="/admin/activity-feed" element={<Protected><ActivityFeed /></Protected>} />
              <Route path="/admin/expenses" element={<Protected><Expenses /></Protected>} />
              <Route path="/admin/backup" element={<Protected><Backup /></Protected>} />
              <Route path="/admin/settings" element={<Protected><SettingsPage /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
